import type { LoyaltyPurchasePoints } from '@libs/common-node';
import type {
    ActivateOffer,
    ActivateOfferResponse,
    LoyaltyMemberPoints,
    LoyaltyOffersResponse,
    PointsModel,
} from '@libs/common-types';
import { AUTH_HEADERS, ensureAuth0AccessToken, logger, type ResponseWithParsedData } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';

function isErrorResponse<T>(response: unknown): response is Error | ResponseWithParsedData<T> {
    return (
        response instanceof Error || (typeof response === 'object' && response !== null && 'parsedResponse' in response)
    );
}

export async function activateOffer(offer: ActivateOffer) {
    const token = await ensureAuth0AccessToken();

    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.activateOffer;
    return await cweaClient.post<
        ActivateOfferResponse,
        Omit<ActivateOffer, 'activationPromoId'> & {
            sourceId: '2801';
            activationPromoId: string;
        }
    >(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
        body: {
            activationPromoId: offer.activationPromoId.toString(),
            intType: offer.intType,
            memberDescriptor: offer.memberDescriptor,
            memberGroup: offer.memberGroup,
            offerId: offer.offerId,
            // [AC-1618]: will be controlled by BFF, can be removed once AC-1618 is complete
            sourceId: '2801',
            transactionType: offer.transactionType,
            version: offer.version,
        },
        useOldFetch: true,
    });
}

// TODO: can be deleted once removed from userSessionWrapper
export async function getLoyaltyPoints(token: string | undefined): Promise<Maybe<LoyaltyPurchasePoints>> {
    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.getLoyaltyPoints;
    const response = await cweaClient.get<LoyaltyPurchasePoints[]>(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
        useOldFetch: true,
    });

    if (!Array.isArray(response)) {
        return null;
    }

    return response.find(points => points.type === 'Purchase_Points');
}

export async function getMemberPoints(): Promise<
    LoyaltyMemberPoints | ResponseWithParsedData<LoyaltyMemberPoints> | null
> {
    try {
        const accessToken = await ensureAuth0AccessToken();
        if (!accessToken) {
            return null;
        }

        const url = CWEA_ENDPOINTS.loyalty.getMemberPoints;
        const response = await cweaClient.get<LoyaltyMemberPoints>(url, {
            headers: {
                [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${accessToken}`,
            },
        });

        if (response instanceof Error) {
            return null;
        }
        if (isErrorResponse<LoyaltyMemberPoints>(response)) {
            return response;
        }
        return response as LoyaltyMemberPoints;
    } catch (error) {
        logger.error('[loyalty.api.getMemberPoints] Request failed', error);
        return null;
    }
}

export async function getOffers(): Promise<LoyaltyOffersResponse | null> {
    try {
        const token = await ensureAuth0AccessToken();

        if (!token) {
            return null;
        }

        const url = CWEA_ENDPOINTS.loyalty.getOffers;
        const response = await cweaClient.get<LoyaltyOffersResponse>(url, {
            headers: {
                [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
            },
        });

        if (isErrorResponse<LoyaltyOffersResponse>(response)) {
            return null;
        }
        return response as LoyaltyOffersResponse;
    } catch (error) {
        logger.error('[loyalty.api.getOffers] Request failed', error);
        return null;
    }
}

export async function getPointsHistory(): Promise<PointsModel[] | null> {
    try {
        const token = await ensureAuth0AccessToken();

        if (!token) {
            return null;
        }

        const url = CWEA_ENDPOINTS.loyalty.getPointsHistory;
        const response = await cweaClient.get<PointsModel[]>(url, {
            headers: {
                [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
            },
        });

        if (isErrorResponse<PointsModel[]>(response)) {
            return null;
        }
        return response as PointsModel[];
    } catch (error) {
        logger.error('[loyalty.api.getPointsHistory] Request failed', error);
        return null;
    }
}
